import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  House, AppWindow, LinkSimple, Buildings, Broadcast, Receipt, SignOut, Sparkle
} from "@phosphor-icons/react";
import { Toaster } from "@/components/ui/sonner";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: House, testId: "nav-dashboard" },
  { to: "/applications", label: "Applications", icon: AppWindow, testId: "nav-applications" },
  { to: "/authorize-builder", label: "Authorize URL", icon: LinkSimple, testId: "nav-authorize-builder" },
  { to: "/submerchants", label: "Sub-merchants", icon: Buildings, testId: "nav-submerchants" },
  { to: "/webhooks", label: "Webhooks", icon: Broadcast, testId: "nav-webhooks" },
  { to: "/audit-log", label: "Audit Log", icon: Receipt, testId: "nav-audit" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const handleLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-[#E2E8F0] bg-white lg:flex lg:flex-col" data-testid="sidebar">
        <Link to="/dashboard" className="flex items-center gap-2.5 px-6 pt-7 pb-8" data-testid="brand-link">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#FF6B00] brand-glow">
            <Sparkle weight="fill" size={20} className="text-white" />
          </div>
          <div className="leading-tight">
            <div className="font-display text-[17px] font-bold text-[#0F172A]">myGenie</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Partners</div>
          </div>
        </Link>

        <nav className="flex-1 px-3 space-y-0.5">
          <div className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">Workspace</div>
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testId}
                className={({ isActive }) =>
                  `group flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-[#FFF3E0] text-[#FF6B00] font-medium"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`
                }
              >
                <Icon size={18} weight={"regular"} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-[#E2E8F0] p-4">
          <div className="flex items-center gap-3 rounded-md px-2 py-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#10B981] text-white text-sm font-semibold">
              {(user?.name || "P")[0]?.toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-[#0F172A]" data-testid="sidebar-user-name">{user?.name}</div>
              <div className="truncate text-xs text-slate-500" data-testid="sidebar-user-email">{user?.email}</div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
              title="Sign out"
            >
              <SignOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-20 flex h-14 items-center justify-between border-b border-[#E2E8F0] bg-white px-4">
        <Link to="/dashboard" className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[#FF6B00]"><Sparkle weight="fill" size={14} className="text-white" /></div>
          <span className="font-display text-sm font-bold">myGenie</span>
        </Link>
        <button onClick={handleLogout} className="text-sm text-slate-500" data-testid="mobile-logout">Sign out</button>
      </header>

      <main className="flex-1 lg:pl-64 pt-14 lg:pt-0">
        <div className="mx-auto w-full max-w-[1280px] px-6 py-8 lg:px-10 lg:py-10">
          {children}
        </div>
      </main>

      <Toaster richColors position="top-right" />
    </div>
  );
}
